import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PHASES_OF_PLAY, PHASE_OF_PLAY_META } from '@4cm/core/phaseOfPlay';
import type { PhaseCoverage, PhaseOfPlay } from '@4cm/core/phaseOfPlay';
import {
  BrainOrb,
  BrainPrompt,
  Button,
  Calibrated,
  Card,
  ChipStrip,
  CornerTag,
  GlassPill,
  Radar,
  SeverityBanner,
  Sheet,
  SpeakButton,
} from '../components/primitives';
import type { Command } from '../components/primitives';
import { NavIcon } from '../components/NavIcon';
import { Pitch } from '../components/Pitch';
import { MetricTile } from '../components/MetricTile';
import { DraftCard } from '../components/DraftCard';
import { WeekStrip } from '../components/WeekStrip';
import { useCalibration } from '../contexts/CalibrationContext';
import { usePageCommands } from '../contexts/BrainBarContext';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useNowSession } from '../lib/queries/useNowSession';
import { useSessionDrills } from '../lib/queries/useSessionDrills';
import { useTeamMetrics } from '../lib/queries/useTeamMetrics';
import { useWeekStrip } from '../lib/queries/useWeekStrip';
import { useTeamRadar } from '../lib/queries/useTeamRadar';
import { useBrain } from '../lib/brain';
import { layoutForDrill } from '../lib/pitchLayouts';
import { HOME_COMMANDS } from '../lib/brainBarDefaults';
import { useVoiceToText } from '../lib/voiceInput';
import { useRegion } from '../lib/i18n/useRegion';
import './Home.css';

type PhaseTotals = Record<PhaseOfPlay, number>;

const EMPTY_PHASE_TOTALS: PhaseTotals = {
  attacking: 0,
  defending: 0,
  transitions: 0,
  individual: 0,
};

const HOME_SCREEN = { kind: 'unknown' } as const;

function isPhaseOfPlay(value: string): value is PhaseOfPlay {
  return PHASES_OF_PLAY.includes(value as PhaseOfPlay);
}

function topPhase(coverage: Partial<PhaseCoverage> | PhaseTotals | null | undefined): PhaseOfPlay | null {
  if (!coverage) return null;
  let best: PhaseOfPlay | null = null;
  let bestValue = 0;
  for (const phase of PHASES_OF_PLAY) {
    const value = coverage[phase] ?? 0;
    if (value > bestValue) {
      best = phase;
      bestValue = value;
    }
  }
  return best;
}

function phaseTotalPct(totals: PhaseTotals, phase: PhaseOfPlay, fallbackCoverage: Partial<PhaseCoverage> | null | undefined): number {
  const total = PHASES_OF_PLAY.reduce((sum, key) => sum + totals[key], 0);
  if (total > 0) return Math.round((totals[phase] / total) * 100);
  return Math.round(fallbackCoverage?.[phase] ?? 0);
}

function phaseChipLabel(phase: PhaseOfPlay, pct: number): string {
  return pct > 0 ? `${phase} ${pct}%` : phase;
}

export function Home() {
  const navigate = useNavigate();
  const { level } = useCalibration();
  const { terms } = useRegion();
  const { team } = useActiveTeam();
  const { session } = useNowSession(team?.id);
  const { drills } = useSessionDrills(session?.id);
  const metrics = useTeamMetrics(team?.id);
  const week = useWeekStrip(team?.id);
  const radar = useTeamRadar(team?.id);
  const brain = useBrain({ teamId: team?.id ?? null, screen: HOME_SCREEN });
  const voice = useVoiceToText({ onTranscript: (transcript) => void brain.send(transcript) });
  const [selectedPhase, setSelectedPhase] = useState<PhaseOfPlay | null>(null);

  // Onboarding is a destination, not a gate. Coaches who land on Home
  // without a team see the wireframe-default Home — they can hit
  // /onboarding via the Brain Bar GO command or the empty-state nudge.
  // Auto-redirect was creating a loop with the Skip → Today escape hatch.

  const isAcademy = level === 'academy';
  const firstDrill = drills[0]?.drill ?? null;
  const pitchLayout = useMemo(() => firstDrill ? layoutForDrill({
    name: firstDrill.name,
    primary_phase: firstDrill.primary_phase,
    sub_themes: firstDrill.sub_themes,
  }) : null, [firstDrill]);
  const weekPhaseTotals = useMemo<PhaseTotals>(() => {
    const totals = { ...EMPTY_PHASE_TOTALS };
    for (const day of week) {
      for (const phase of PHASES_OF_PLAY) {
        totals[phase] += day.phaseCoverage[phase] ?? 0;
      }
    }
    return totals;
  }, [week]);
  const sessionPhase = firstDrill?.primary_phase ?? topPhase(session?.phase_coverage) ?? 'attacking';
  const activePhase = selectedPhase ?? topPhase(weekPhaseTotals) ?? sessionPhase;
  const activePhasePct = phaseTotalPct(weekPhaseTotals, activePhase, session?.phase_coverage);
  const primaryPhaseMeta = PHASE_OF_PLAY_META[sessionPhase];
  const activePhaseMeta = PHASE_OF_PLAY_META[activePhase];
  const curatorLabel = firstDrill?.source_type === 'CURATED_EXPERT'
    ? `Coach's Corner${firstDrill.curator_name ? ` · ${firstDrill.curator_name}` : ''}`
    : null;

  const pageCommands = useMemo<Command[]>(
    () => HOME_COMMANDS.build({ send: (t) => void brain.send(t), isAcademy }),
    [brain.send, isAcademy],
  );

  usePageCommands(pageCommands);

  const sessionTitle = session?.title ?? (isAcademy ? '8v8 transition — vertical first 3s' : 'Wide overload 4v4 — width on the switch');
  const sessionDuration = session?.duration_mins ?? (isAcademy ? 35 : 25);
  const drillCaption = firstDrill?.name?.toUpperCase() ?? `${primaryPhaseMeta.label.toUpperCase()} · ${isAcademy ? 'MD-3' : 'TONIGHT'}`;
  const sessionPath = session ? `/sessions/${session.id}` : null;
  const runPath = session ? `/sessions/${session.id}/run` : null;
  const tactics3dPath = session ? `/sessions/${session.id}/3d` : null;
  const primaryActionLabel = session ? (isAcademy ? 'Open in 3D' : `Run on ${terms.field}`) : 'Plan tonight';
  const primaryActionPath = isAcademy ? (tactics3dPath ?? '/sessions/new') : (runPath ?? '/sessions/new');
  const handlePrimaryAction = () => navigate(primaryActionPath);

  return (
    <>
      <main className="main page">
        <div className="greeting">
          <h1 className="greeting__h">
            {isAcademy ? (
              <>Saturday's plan vs <b>Whitfield</b> is set, Josh.<br/>One drill might be <em>off-cycle for MD-3</em>.</>
            ) : (
              <>Tonight's session is set, <b>Josh.</b><br/>Three drills, one feels <em>a little soft</em>.</>
            )}
          </h1>
          <span className="greeting__tag">{isAcademy ? 'MD-3 · TRANSITIONS' : 'TRAINING NIGHT'}</span>
        </div>

        <Calibrated min="ADVANCED">
          <div className="tiles">
            <MetricTile
              label="Sessions this week"
              value={String(metrics.sessionsThisWeek).padStart(2, '0')}
              sub={<>Across this micro-cycle</>}
              spark="line"
            />
            <MetricTile
              label="Drill minutes planned"
              value={String(metrics.drillMinutesPlanned)}
              unit="min"
              sub={<>Across all drills this week</>}
              spark="bars"
            />
            <MetricTile
              label="Squad availability"
              value={String(metrics.squadAvailable)}
              unit={`/${metrics.squadTotal || metrics.squadAvailable}`}
              sub={<>{metrics.squadLimited > 0 ? <span className="warn">{metrics.squadLimited} limited</span> : '0 limited'} · 0 unavailable</>}
              spark="dots"
            />
          </div>
        </Calibrated>

        <section className="brain-row" aria-label="Brain prompt">
          <div className={`brain-card-wrap${isAcademy ? ' brain-card-wrap--narrow' : ''}`}>
            <BrainPrompt
              ambient={!isAcademy}
              onSubmit={(text) => void brain.send(text)}
              listening={voice.listening}
              voiceStatus={voice.status}
              onMicStart={() => void voice.start()}
              onMicStop={voice.stop}
            />
          </div>
        </section>

        <section className="now-row" aria-label="Tonight's session">
          <Card variant="glass" className="now-card">
            <div className="now-card__pitch">
              <Pitch
                caption={drillCaption}
                width={360}
                height={200}
                positions={pitchLayout ?? undefined}
              />
            </div>
            <div className="now-card__body">
              <div className="now-title-row">
                <h2 className="now-title">{sessionTitle}</h2>
                <SpeakButton
                  text={`${sessionTitle}. ${session?.psych_social_intent ?? (isAcademy
                    ? 'High-load build, 8v8 transition, P3 unit. Trigger on the third defensive recovery; coach the cover-shadow on the second man.'
                    : 'Possession with a wide rule. Inside player only joins on the third pass. Coaching point: third-man run when the ball goes wide.')}`}
                  ariaLabel="Read tonight's session aloud"
                  chunked
                />
              </div>
              <p className="now-sub">
                {session?.psych_social_intent ?? (isAcademy
                  ? 'High-load build → 8v8 transition → P3 unit. Trigger on the third defensive recovery; coach the cover-shadow on the second man.'
                  : 'Possession with a wide rule. Inside player only joins on the third pass. Coaching point: third-man run when the ball goes wide.')}
              </p>
              <div className="now-pills">
                <GlassPill mono><span className="pill-dot pill-dot--accent" />{primaryPhaseMeta.label}</GlassPill>
                <GlassPill mono><span className="pill-dot" />{team?.age_group ?? (isAcademy ? 'U17' : 'U12')}</GlassPill>
                <GlassPill mono><span className="pill-dot" />{sessionDuration} min</GlassPill>
                <GlassPill mono><span className="pill-dot" />{isAcademy ? '8v8' : '4v4'}</GlassPill>
                {curatorLabel && <GlassPill mono><span className="pill-dot pill-dot--accent" />{curatorLabel}</GlassPill>}
              </div>
              <div className="now-actions">
                <Button variant="primary" size="touchline" onClick={handlePrimaryAction}>
                  {primaryActionLabel} <NavIcon name="arrow" />
                </Button>
                {!isAcademy && tactics3dPath && (
                  <Button variant="glass" size="touchline" onClick={() => navigate(tactics3dPath)}>
                    Open in 3D
                  </Button>
                )}
                {isAcademy && sessionPath && (
                  <Button variant="glass" size="touchline" onClick={() => navigate(sessionPath)}>
                    Designer
                  </Button>
                )}
                {sessionPath && (
                  <Button variant="glass" size="touchline" onClick={() => navigate(sessionPath)}>
                    Open session
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </section>

        <Calibrated min="COMPETITIVE">
          <section aria-label="Phase-of-play filters">
            <ChipStrip
              chips={PHASES_OF_PLAY.map((phase) => ({
                label: phaseChipLabel(phase, phaseTotalPct(weekPhaseTotals, phase, session?.phase_coverage)),
                value: phase,
                active: activePhase === phase,
              }))}
              onSelect={(value) => {
                if (isPhaseOfPlay(value)) setSelectedPhase(value);
              }}
            />
          </section>
        </Calibrated>

        <Calibrated min="COMPETITIVE">
          {week.length > 0 && (
            <section className="week-row" aria-label="This week">
              <WeekStrip days={week} activePhase={activePhase} />
              <SeverityBanner pct={activePhasePct} label={`${activePhaseMeta.label.toLowerCase()} · this week`} />
            </section>
          )}
        </Calibrated>

        <section className="insight-row" aria-label="Last week's insight">
          {isAcademy ? (
            <>
              <GlassPill mono>
                rolling: <strong>{activePhaseMeta.label.toLowerCase()} {activePhasePct}%</strong> · phase-of-play primary
              </GlassPill>
              <SpeakButton
                text={`Rolling phase-of-play coverage: ${activePhaseMeta.label.toLowerCase()} ${activePhasePct} percent this week.`}
                ariaLabel="Read this week's coverage aloud"
              />
            </>
          ) : (
            <>
              <GlassPill mono>last week — pressing trigger improved</GlassPill>
              <SpeakButton
                text="Last week, your pressing trigger improved."
                ariaLabel="Read last-week insight aloud"
              />
            </>
          )}
        </section>
      </main>

      <Sheet side="right" label="AI assistant" className="rail">
        <div className="rail-head">
          <div className="rail-title">
            <BrainOrb size="md" state={brain.loading ? 'thinking' : 'idle'} ariaLabel="Brain" />
            <span>Brain</span>
            {brain.status && <span className="rail-status">· {brain.status}</span>}
          </div>
        </div>

        {brain.turns.length > 0 ? (
          <div className="agent-thread">
            {brain.turns.map((turn) => (
              turn.draft ? (
                <DraftCard
                  key={turn.id}
                  type={turn.draft.type}
                  payload={turn.draft.payload}
                  status={turn.status}
                  onApprove={() => void brain.approveDraft(turn.id)}
                  onDismiss={() => brain.dismissDraft(turn.id)}
                />
              ) : (
                <div
                  key={turn.id}
                  className={`agent-row agent-row--${turn.role === 'user' ? 'user' : 'bot'}`}
                >
                  {turn.role !== 'user' && (
                    <BrainOrb size="xs" ariaLabel="Brain" />
                  )}
                  <div className={`agent-msg agent-msg--${turn.role === 'user' ? 'user' : 'bot'}`}>
                    {turn.text}
                  </div>
                </div>
              )
            ))}
          </div>
        ) : isAcademy ? (
          <>
            <div className="rail-radar">
              <Radar values={radar} size={160} />
            </div>
            <div className="rail-section-label">Brain noticed</div>
            <ul className="rail-list">
              <li>
                <CornerTag corner="tech" label="shape" filled />
                <span>Ask Brain about today's session focus.</span>
              </li>
            </ul>
          </>
        ) : (
          <div className="agent-thread">
            <div className="agent-msg agent-msg--bot">
              Ask Brain anything about tonight's session, or say what's on your mind.
            </div>
          </div>
        )}
      </Sheet>
    </>
  );
}
